import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, TextField, Button, Checkbox, FormControlLabel, Stack, MenuItem } from "@mui/material";
import Grid from '@mui/material/Grid2';
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { toast } from "react-toastify";
import instance from "../../Utils/api";


// Define the initial state structure outside the component for reusability
const INITIAL_STATE = {
  name: "", mobile: "", state: "", city: "",
  line_1: "", line_2: "", landmark: "", pincode: "",
  address_type: "", default: false, address_id: "",
};

const Addaddress = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A single, generic change handler for text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Specific handler for the 'default' checkbox
  const handleCheckboxChange = (e) => {
    setData((prevData) => ({ ...prevData, default: e.target.checked }));
  };

  // Fetch address data when in "edit" mode
  const handleAddressData = useCallback(async () => {
    if (!id) return; // Only run if there is an ID
    try {
      const response = await instance.get(`Address/getAddressById?addres_id=${id}`);
      const addressData = response.data.data;
      if (addressData) {
        setData({ ...INITIAL_STATE, ...addressData, address_id: id });
      }
    } catch (error) {
      console.error("Failed to fetch address:", error);
      toast.error("Could not load address details.");
    }
  }, [id]);

  useEffect(() => {
    handleAddressData();
  }, [handleAddressData]);

  // Unified validation logic
  const validateForm = () => {
    const requiredFields = [
      'name', 'mobile', 'state', 'city',
      'line_1', 'landmark', 'pincode'
    ];

    for (const field of requiredFields) {
      if (!data[field]) {
        toast.error(`Please fill the ${field.replace('_', ' ')} field.`);
        return false;
      }
    }
    if (data.mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return false;
    }
    return true;
  };

  // Unified save/update handler
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let response;
      if (id) { // Update existing address
        response = await instance.put("Address/editAddress", data);
        toast.success(response.data.message || "Address updated successfully!");
        navigate(`/profile/address`);
      } else { // Add new address
        response = await instance.post("Address/addAddress", data);
        toast.success("Address added successfully!");
        navigate("/profile/address");
      }
    } catch (error) {
      console.error("Failed to save address:", error);
      toast.error(error.response?.data?.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Geolocation logic remains the same but moved inside for clarity
  const getLocation = () => {
    // if (!navigator.geolocation) {
    //   toast.error("Geolocation is not supported by your browser.");
    //   return;
    // }

    const successCallback = async (position) => {
      // SECURITY WARNING: Storing API keys in frontend code is highly insecure and can lead to misuse.
      // Use environment variables and a backend proxy server to protect your key.
      const API_KEY = "AIzaSyAW-xZdNAskSJteiT9GmKfnFYRqfeZcWkc"; // Replace with process.env.REACT_APP_GOOGLE_API_KEY
      const { latitude, longitude } = position.coords;

      try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${API_KEY}`);
        const responseData = await response.json();

        if (responseData.status === "OK" && responseData.results.length > 0) {
          const components = responseData.results[0].address_components;
          const get = (type) => components.find(c => c.types.includes(type))?.long_name || "";

          setData(prev => ({
            ...prev,
            state: get("administrative_area_level_1"),
            city: get("locality"),
            line_1: `${get("street_number")} ${get("route")}`.trim(),
            pincode: get("postal_code"),
            landmark: get("sublocality_level_1"),
          }));
          toast.success("Location fetched successfully!");
        } else {
          toast.error("Unable to fetch address. Try again.");
        }
      } catch (error) {
        toast.error("Failed to fetch address.");
      }
    };

    const errorCallback = (error) =>

      navigator.geolocation.getCurrentPosition(successCallback, errorCallback, { enableHighAccuracy: true });
  };

  // Define form fields in a configuration array for easy rendering
  const formFields = [
    { name: 'name', label: 'Full Name', placeholder: 'Enter your full name', required: true },
    { name: 'mobile', label: 'Mobile Number', placeholder: 'Enter your mobile number', type: 'tel', maxLength: 10, required: true },
    { name: 'state', label: 'State', placeholder: 'Enter your state', required: true },
    { name: 'line_1', label: 'Address Line 1', placeholder: 'Flat, House no, Building', required: true },
    { name: 'line_2', label: 'Address Line 2', placeholder: 'Area, Street, Sector, Village', required: false },
    { name: 'landmark', label: 'Landmark', placeholder: 'Enter your landmark', required: true },
    { name: 'city', label: 'Town/City', placeholder: 'Enter your town/city', required: true },
    { name: 'pincode', label: 'Pincode', placeholder: 'Enter your pincode', type: 'tel', maxLength: 6, required: true },
    { name: 'address_type', label: 'Address Type', placeholder: 'e.g., Home, Work', required: false },
  ];

  return (
    <section className="address-form-container">
      <form onSubmit={handleSave} noValidate>
        <Stack spacing={4}>
          <div className="form-header">

            <Typography variant="h5">
              {id ? "Manage Your Address" : "Add a New Address"}
            </Typography>

            <Button startIcon={<KeyboardBackspaceIcon />} onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>

          <Grid container spacing={2} className="form-grid">
            {formFields.map(field => (
              <FormField
                key={field.name}
                name={field.name}
                label={field.label}
                value={data[field.name]}
                onChange={handleChange}
                maxLength={field.maxLength}
                required={field.required}
              />
            ))}
          </Grid>

          <div className="form-options">
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.default}
                  onChange={handleCheckboxChange}
                  sx={{ color: "#E37B00", "&.Mui-checked": { color: "#E37B00" } }}
                />
              }
              label="Set this as my default address"
            />
          </div>

          <div className="form-actions">
            {/* <Button
              variant="outlined"
              startIcon={<MyLocationIcon />}
              onClick={getLocation}
              className="location-btn"
            >
              Use My Current Location
            </Button> */}
            <Button
              type="submit"
              variant="contained"
              className="save-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : (id ? "Update Address" : "Add Address")}
            </Button>
          </div>
        </Stack>
      </form>
    </section>
  );
};

// Reusable FormField component
const FormField = ({ name, label, value, onChange, maxLength, required }) => {
  const isNumeric = name === 'mobile' || name === 'pincode';

  const handleNumericChange = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    if (maxLength && onlyNums.length > maxLength) return;
    onChange({ target: { name, value: onlyNums } });
  };

  if (name === "address_type") {
    return (
      <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
        <TextField
          select
          fullWidth
          variant="outlined"
          name={name}
          label={label}
          value={value}
          onChange={onChange}
          size="small"
        >
          <MenuItem value="Home">Home</MenuItem>
          <MenuItem value="Work">Work</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>
      </Grid>
    );
  }

  return (
    <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
      <TextField
        fullWidth
        required={required}
        variant="outlined"
        name={name}
        label={label}
        value={value}
        onChange={isNumeric ? handleNumericChange : onChange}
        type={isNumeric ? "tel" : "text"}
        size="small"
      />
    </Grid>
  );
};

export default Addaddress;
